import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";

/**
 * Module switcher pill row.
 *  - Reads /api/manage/modules (admin sees everything incl. 'system')
 *  - Filters to modules that own the kind of config being viewed (rate_card or rules)
 *  - Selected module is controlled by the parent (URL or local state)
 */
export function ModuleSwitcher({
  value,
  onChange,
  filter = "all",
  includeSystem = false,
}: {
  value: string;
  onChange: (m: string) => void;
  /** Limit to modules that have rate_card / rules / all enabled modules. */
  filter?: "rate_card" | "rules" | "all";
  includeSystem?: boolean;
}) {
  const modules = useQuery({ queryKey: ["modules-admin"], queryFn: api.modulesAdmin });
  const all: any[] = modules.data?.modules ?? [];
  const visible = all
    .filter((m) => includeSystem || m.key !== "system")
    // 'hidden' status means "don't show this anywhere except /modules". Respect that here.
    .filter((m) => m.status !== "hidden")
    .filter((m) => {
      if (filter === "rate_card") return m.has_rate_card;
      if (filter === "rules")     return m.has_rules;
      return true;
    });

  if (!visible.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {visible.map((m) => {
        const isActive = m.key === value;
        const disabled = m.status !== "enabled";
        return (
          <button
            key={m.key}
            type="button"
            onClick={() => onChange(m.key)}
            className={[
              "px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 border transition",
              isActive
                ? "bg-liaise-navy text-white border-liaise-navy"
                : disabled
                  ? "bg-white border-slate-200 text-slate-400"
                  : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50",
            ].join(" ")}
            title={m.description ?? ""}
          >
            <span>{m.icon}</span>
            <span>{m.display_name}</span>
            {disabled && <span className="text-[10px] uppercase tracking-wider opacity-70 ml-1">{m.status}</span>}
          </button>
        );
      })}
    </div>
  );
}
